(() => {
  'use strict';
  if (window.vbBundles) return;
  const root = () => window.Shopify?.routes?.root || '/';
  const config = () => {
    try { return JSON.parse(document.querySelector('[data-vb-bundle-config]')?.textContent || '{}'); }
    catch { return {}; }
  };
  const enabled = () => config().enabled === true;
  const failure = () => new Error(window.Viebys?.strings?.gift_check_cart || 'Les cadeaux du panier doivent être vérifiés.');
  const group = item => item.properties?._vb_bundle_group;
  const role = item => item.properties?._vb_bundle_role;
  const variant = item => String(item.variant_id || item.id);
  const nonce = () => window.crypto?.randomUUID?.() || Date.now().toString(36) + Math.random().toString(36).slice(2);
  let serial = Promise.resolve();
  const enqueue = work => {
    const next = serial.then(work, work);
    serial = next.catch(() => {});
    return next;
  };
  async function request(path, body) {
    // Resolve fetch at request time: Shopify's consent-aware pixel observes the real add.
    const response = await window.fetch(root() + path, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: {Accept:'application/json', ...(body ? {'Content-Type':'application/json','X-Kaching-Cart-Ignore':'1'} : {})},
      ...(body ? {body:JSON.stringify(body)} : {})
    });
    const result = await response.json();
    if (!response.ok || result.status >= 400) throw new Error(result.description || failure().message);
    return result;
  }
  function expectedFor(id) {
    const pack = config().packs?.find(p => String(p.variant) === String(id));
    if (!pack || !Array.isArray(pack.gifts)) throw failure();
    const gifts = pack.gifts.filter(g => Number(g.quantity) > 0);
    if (!gifts.length || gifts.some(g => !/^\d+$/.test(String(g.variant)) || !Number.isSafeInteger(g.quantity))) throw failure();
    if (new Set(gifts.map(g => String(g.variant))).size !== gifts.length) throw failure();
    return {...pack, gifts};
  }
  function properties(id, parent, type, unit) {
    return {_vb_bundle_group:id, _vb_bundle_role:type, _vb_bundle_parent:String(parent),
      ...(type === 'main' ? {_vb_bundle_unit_price:String(unit)} : {})};
  }
  function giftItems(pack, id, quantity) {
    return pack.gifts.map(g => ({id:Number(g.variant), quantity:g.quantity * quantity,
      properties:properties(id, pack.variant, 'gift')}));
  }
  function buildItems(choice) {
    const pack = expectedFor(choice.dataset.vbVariant);
    if (['face','eye','neck'].some(type => Number(choice.dataset['vb'+type[0].toUpperCase()+type.slice(1)]) !== Number(pack[type]))) throw failure();
    const unit = Number(choice.dataset.vbUnitPrice);
    if (!Number.isSafeInteger(unit) || unit <= 0) throw failure();
    const id = nonce();
    return {id, items:[{id:Number(pack.variant),quantity:1,properties:properties(id,pack.variant,'main',unit)},...giftItems(pack,id,1)]};
  }
  function mainState(cart, id) {
    const lines = cart.items.filter(i => group(i) === id);
    const mains = lines.filter(i => role(i) === 'main');
    if (!id || !mains.length) throw failure();
    const main = mains[0], pack = expectedFor(variant(main));
    const unit = Number(main.properties._vb_bundle_unit_price);
    const signature = item => JSON.stringify(Object.entries(item.properties || {}).sort(([a],[b]) => a.localeCompare(b)));
    const sellingPlan = item => String(item.selling_plan_allocation?.selling_plan?.id || '');
    const props = signature(main), plan = sellingPlan(main);
    let quantity = 0;
    for (const item of mains) {
      if (variant(item) !== variant(main) || signature(item) !== props || sellingPlan(item) !== plan ||
          String(item.properties._vb_bundle_parent) !== variant(main) ||
          !Number.isSafeInteger(item.quantity) || item.quantity < 1 ||
          !Number.isSafeInteger(unit) || unit <= 0 || item.original_line_price !== unit * item.quantity ||
          !Number.isSafeInteger(item.final_line_price) || item.final_line_price < 0 || item.final_line_price > item.original_line_price) throw failure();
      quantity += item.quantity;
    }
    if (!Number.isSafeInteger(quantity)) throw failure();
    const gifts = lines.filter(i => role(i) !== 'main');
    for (const item of gifts) {
      if (role(item) !== 'gift' || String(item.properties._vb_bundle_parent) !== variant(main) ||
          !pack.gifts.some(g => String(g.variant) === variant(item)) ||
          !Number.isSafeInteger(item.quantity) || item.quantity < 1) throw failure();
    }
    return {main,mains,gifts,pack,quantity};
  }
  function verifyCart(cart, onlyGroup) {
    if (!Array.isArray(cart?.items)) throw failure();
    const managed = cart.items.filter(i => group(i) && (!onlyGroup || group(i) === onlyGroup));
    const groups = new Set(managed.map(group));
    if (onlyGroup && !groups.has(onlyGroup)) throw failure();
    for (const id of groups) {
      const {gifts,pack,quantity} = mainState(cart,id);
      const totals = new Map();
      for (const item of gifts) {
        if (item.final_line_price !== 0) throw failure();
        totals.set(variant(item), (totals.get(variant(item)) || 0) + item.quantity);
      }
      if (totals.size !== pack.gifts.length || pack.gifts.some(g => totals.get(String(g.variant)) !== g.quantity * quantity)) throw failure();
    }
    return cart;
  }
  async function removeGroup(id) {
    const cart = await request('cart.js');
    const lines = cart.items.filter(i => group(i) === id);
    if (lines.length) await request('cart/update.js', {updates:Object.fromEntries(lines.map(i => [i.key,0]))});
    const after = await request('cart.js');
    if (after.items.some(i => group(i) === id)) throw failure();
    return after;
  }
  function cartFromSections(sections) {
    if (!sections || typeof sections !== 'object') return null;
    for (const html of Object.values(sections)) {
      if (typeof html !== 'string') continue;
      try {
        const state = new DOMParser().parseFromString(html, 'text/html').querySelector('script[data-vb-cart-state]');
        if (!state) continue;
        const cart = JSON.parse(state.textContent);
        if (Array.isArray(cart?.items) && Number.isSafeInteger(cart.item_count) && cart.item_count >= 0 &&
            Number.isSafeInteger(cart.total_price) && cart.total_price >= 0) return cart;
      } catch {}
    }
    return null;
  }
  async function add(choice, rendering = {}) {
    const built = buildItems(choice);
    try {
      const result = await request('cart/add.js', {items:built.items,
        ...(rendering.sections ? {sections:rendering.sections} : {}),
        ...(rendering.sections_url ? {sections_url:rendering.sections_url} : {})});
      // Section rendering can fail after the add has already succeeded; do not repeat it.
      const renderedCart = cartFromSections(result.sections);
      const cart = verifyCart(renderedCart || await request('cart.js'), built.id);
      const synced = await sync(cart);
      if (renderedCart && synced === cart) synced.sections = result.sections;
      else delete synced.sections;
      return synced;
    } catch (error) {
      // Never retry a possibly committed add, and never clear unrelated cart items.
      try { await removeGroup(built.id); }
      catch { document.documentElement.dataset.vbBundleUnsafe = 'true'; }
      throw error;
    }
  }
  async function reconcile(cart) {
    const ids = new Set(cart.items.filter(i => group(i) && role(i) === 'main').map(group));
    // Validate every parent before changing any group, including orphan removal.
    const states = Array.from(ids, id => ({id,...mainState(cart,id)}));
    const changes = {};
    cart.items.filter(i => group(i) && role(i) === 'gift' && !ids.has(group(i)))
      .forEach(i => { changes[i.key] = 0; });
    const missing = [];
    for (const {id,mains,gifts,pack,quantity} of states) {
      // Shopify can split identical parents when its discount allocation changes.
      // Consolidate those rows and resize the gifts in the same request.
      if (mains.length > 1) mains.forEach((i,index) => {changes[i.key] = index ? 0 : quantity;});
      for (const gift of pack.gifts) {
        const matches = gifts.filter(i => variant(i) === String(gift.variant));
        const target = gift.quantity * quantity;
        const total = matches.reduce((sum,i) => sum+i.quantity,0);
        if (!matches.length) missing.push({id:Number(gift.variant),quantity:target,properties:properties(id,pack.variant,'gift')});
        else if (total !== target) matches.forEach((i,index) => {changes[i.key] = index ? 0 : target;});
      }
    }
    if (Object.keys(changes).length) cart = await request('cart/update.js', {updates:changes});
    if (missing.length) {
      await request('cart/add.js', {items:missing});
      cart = await request('cart.js');
    }
    return cart;
  }
  async function adoptLegacy(cart) {
    const packIds = new Set(config().packs?.map(p => String(p.variant)) || []);
    for (const previous of cart.items.filter(i => !group(i) && !i.properties?._vb_welcome && packIds.has(variant(i)))) {
      // Existing carts and ordinary add buttons receive the same native gifts.
      // A pre-existing app-owned bundle must not be silently converted.
      if (previous.properties?.__kaching_bundles) throw failure();
      const id = nonce(), pack = expectedFor(variant(previous));
      const props = {...previous.properties,...properties(id,pack.variant,'main',previous.original_price)};
      try {
        await request('cart/change.js',{id:previous.key,quantity:previous.quantity,properties:props});
        await request('cart/add.js',{items:giftItems(pack,id,previous.quantity)});
        cart = verifyCart(await request('cart.js'),id);
      } catch (error) {
        try {
          const now = await request('cart.js');
          const gifts = now.items.filter(i => group(i) === id && role(i) === 'gift');
          if(gifts.length) await request('cart/update.js',{updates:Object.fromEntries(gifts.map(i=>[i.key,0]))});
          const restored = await request('cart.js');
          const main = restored.items.find(i => group(i) === id && role(i) === 'main');
          if(main) await request('cart/change.js',{id:main.key,quantity:main.quantity,properties:previous.properties||{}});
        } catch { document.documentElement.dataset.vbBundleUnsafe = 'true'; }
        throw error;
      }
    }
    return cart;
  }
  async function sync(cart) {
    cart = await adoptLegacy(cart || await request('cart.js'));
    cart = verifyCart(await reconcile(cart));
    delete document.documentElement.dataset.vbBundleUnsafe;
    return cart;
  }
  async function setQuantity(id, quantity) {
    const before = await request('cart.js');
    if (!before.items.some(i => group(i) === id && role(i) === 'main')) return sync(before);
    const {mains,gifts,pack} = mainState(before,id);
    if (!quantity) return sync(await removeGroup(id));
    const updates = Object.fromEntries(mains.map((i,index) => [i.key,index ? 0 : quantity]));
    for (const gift of pack.gifts) {
      const matches = gifts.filter(i => variant(i) === String(gift.variant));
      matches.forEach((i,index) => {updates[i.key] = index ? 0 : gift.quantity * quantity;});
    }
    return sync(await request('cart/update.js',{updates}));
  }
  async function change(id, quantity) {
    if (!Number.isSafeInteger(quantity) || quantity < 0) throw failure();
    const before = await request('cart.js');
    const previous = before.items.some(i => group(i) === id && role(i) === 'main') ? mainState(before,id) : null;
    try { return await setQuantity(id,quantity); }
    catch(error) {
      try {
        if(previous && quantity > 0) await setQuantity(id,previous.quantity);
        else if(quantity === 0) await removeGroup(id);
      } catch { document.documentElement.dataset.vbBundleUnsafe = 'true'; }
      throw error;
    }
  }
  window.vbBundles = {
    get enabled() { return enabled(); },
    add: (choice,rendering) => enqueue(() => add(choice,rendering)),
    change: (id,quantity) => enqueue(() => change(id,quantity)),
    sync: cart => enqueue(() => sync(cart)),
    verifyCart, buildItems
  };
  let checkingCheckout = false;
  async function saveCartForm(form) {
    if (!form) return;
    const fields = new FormData(form);
    const body = {};
    const inputs = Array.from(form.elements).filter(field => field.name === 'updates[]' && !field.disabled);
    if (inputs.length) {
      const cart = await request('cart.js');
      const updates = {}, groups = new Map(), seen = new Set();
      for (const field of inputs) {
        const key = field.dataset.vbNativeLineKey;
        const item = cart.items.find(line => line.key === key);
        const value = Number(field.value), initial = Number(field.defaultValue);
        if (!key || seen.has(key) || !field.value.trim() || !Number.isSafeInteger(value) || value < 0 ||
            !Number.isSafeInteger(initial) || initial < 0) throw failure();
        seen.add(key);
        const id = field.dataset.vbNativeGroup || (item && group(item));
        if (id) {
          const state = mainState(cart,id);
          const kind = field.dataset.vbNativeRole || (item && role(item));
          const fieldVariant = field.dataset.vbNativeVariant || (item && variant(item));
          if (item && group(item) !== id) throw failure();
          if ((kind !== 'main' && kind !== 'gift') ||
              (kind === 'main' && String(fieldVariant) !== variant(state.main)) ||
              (kind === 'gift' && !state.pack.gifts.some(g => String(g.variant) === String(fieldVariant)))) throw failure();
          const entry = groups.get(id) || {state,initial:0,desired:0,parents:0,changed:false};
          if (kind === 'main') {
            entry.initial += initial; entry.desired += value; entry.parents += 1;
          }
          entry.changed ||= field.value !== field.defaultValue;
          groups.set(id,entry);
        } else {
          if (!item) throw failure();
          if (field.value !== field.defaultValue) {
            if (item.quantity !== initial && item.quantity !== value) throw failure();
            updates[key] = value;
          }
        }
      }
      for (const entry of groups.values()) {
        if (!entry.parents || !Number.isSafeInteger(entry.initial) || !Number.isSafeInteger(entry.desired)) throw failure();
        if (!entry.changed) continue;
        const {state} = entry;
        // Dawn may already have applied the input, splitting the discount's X lines.
        if (state.quantity !== entry.initial && state.quantity !== entry.desired) throw failure();
        state.mains.forEach((line,index) => {updates[line.key] = index ? 0 : entry.desired;});
        for (const gift of state.pack.gifts) {
          const matching = state.gifts.filter(line => variant(line) === String(gift.variant));
          matching.forEach((line,index) => {updates[line.key] = index ? 0 : gift.quantity * entry.desired;});
        }
      }
      if (Object.keys(updates).length) body.updates = updates;
    }
    for (const [name,value] of fields) {
      if (name === 'note') body.note = String(value);
      const attribute = name.match(/^attributes\[(.+)\]$/);
      if (attribute) (body.attributes ||= {})[attribute[1]] = String(value);
    }
    if (Object.keys(body).length) await request('cart/update.js',body);
  }
  async function checkout(event, destination, form) {
    if (!enabled()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkingCheckout || document.querySelector('vb-cart-drawer [aria-busy="true"]')) return;
    checkingCheckout = true;
    try {
      await saveCartForm(form);
      let cart = await window.vbBundles.sync();
      if(window.vbWelcome) cart = await window.vbWelcome.cleanup(cart);
      verifyCart(cart);
      window.location.assign(destination);
    } catch {
      await window.vbCart?.open();
    } finally { checkingCheckout = false; }
  }
  document.addEventListener('click',event => {
    if (!(event.target instanceof Element) || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href],button[name="checkout"],[data-vb-cart-checkout]');
    if (!link) return;
    const destination = new URL(link.getAttribute('href') || root()+'checkout',window.location.href);
    if (destination.origin === window.location.origin && /\/checkout(?:\/|$)/.test(destination.pathname)) checkout(event,destination.href,link.form || link.closest('form'));
  },true);
  document.addEventListener('submit',event => {
    if(event.submitter?.name === 'checkout') checkout(event,root()+'checkout',event.target);
  },true);
})();
